import { ChangeDetectionStrategy, Component, EventEmitter, OnInit, Output, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MenuItem, TreeNode } from 'primeng/api';
import { GridItemResizedMessage } from 'src/app/shared/models/mediator-messages.model';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { LauncherSharedService } from '../../services/launcher-shared.service';
import { StoreService } from '../../services/store.service';
import { Store } from '@ngneat/elf';
import { SidebarModelUi } from '../../models/sidebar.model';

@Component({
    selector: 'app-sidebar-menu',
    templateUrl: './sidebar-menu.component.html',
    styleUrls: ['./sidebar-menu.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidebarMenuComponent implements OnInit {

  public activeItem: MenuItem;
  public catalogSubMenu: TreeNode[];
  public navbarItems: MenuItem[];
  public wellboreInputsSubMenu: TreeNode[];
  public stressSubMenu: TreeNode[];
  public thermalSubMenu: TreeNode[];
  public apbSubMenu: TreeNode[];
  public perimotSubMenu: TreeNode[];
  public sidebarStore: Store;
  public isLoading = signal<boolean>(false);
  
  // Signals for reactive state (Angular 21 zoneless compatibility)
  public menuSelected = signal<string>('');
  public sidebarOpen = signal<boolean>(false);
  public scrollToNode = signal<boolean>(false);
  public hovered = signal<boolean>(false);

  @Output()
  public closeSidenav: EventEmitter<string>;

  constructor(
    private _messenger: MediatorService,
    private _route: ActivatedRoute,
    private _launcherSharedService: LauncherSharedService,
    private _storeService: StoreService
  ) {
    this.closeSidenav = new EventEmitter();
    this.isLoading.set(true);
    this.navbarItems = [
      { label: 'Well Explorer', iconClass: 'wellexp-ico', command: () => this.setMenuComponent('wellExplorer') },
      { label: 'PERIFOR Inputs', iconClass:'perifor-ico', command: () => this.setMenuComponent('wellboreInputs') },
      { label: 'PERICAL (Thermal)', iconClass:'perical-ico', command: () => this.setMenuComponent('thermal') },
      { label: 'PERINIX (APB)', iconClass: 'perinix-ico', command: () => this.setMenuComponent('apb') },
      { label: 'PERIVIS (Stress)', iconClass:'perivis-ico', command: () => this.setMenuComponent('stress') },
      // { label: 'PERIMOT (WHM)', iconClass:'perimot-ico', command: () => this.setMenuComponent('perimot') },
      { label: 'Catalogs', iconClass: 'catalog-ico', command: () => this.setMenuComponent('database') },
    ];

    this.catalogSubMenu = [
      { data: { label: 'Formations', identifier: 'formationsCatalog', icon: '../../assets/icons/formations-24x24.svg' } },
      { data: { label: 'Hard Cement', identifier: 'hardCementCatalog', icon: '../../assets/icons/hard-cement-24x24.svg' } },
      {
        label: 'Pipes', type: 'group', expandedIcon: 'pi pi-folder-open', collapsedIcon: 'pi pi-folder', expanded: true, children: [
          { data: { label: 'Temperature Deration', identifier: 'temperatureDeration', icon: '../../assets/icons/undisturbed-temperature-24x24.svg' } },
          { data: { label: 'Grades', identifier: 'gradesCatalog', icon: '../../assets/icons/settings-24x24.svg' } },
          { data: { label: 'Pipes', identifier: 'pipesCatalog', icon: '../../assets/icons/pipes-24x24.svg' } },
          { data: { label: 'Connections', identifier: 'connectionsCatalog', icon: '../../assets/icons/pipes-24x24.svg' } }
        ]
      }
    ];

    this.wellboreInputsSubMenu = [
      { data: { label: 'Well Type & Datums', identifier: 'wellType', icon: '../../assets/icons/well-type-24x24.svg' } },
      {
        label: 'Trajectory', type: 'group', expandedIcon: 'pi pi-folder-open', collapsedIcon: 'pi pi-folder', expanded: true, children: [
          { data: { label: 'Trajectory Input', identifier: 'trajectoryGrid', icon: '../../assets/icons/trajectories-24x24.svg' } },
          { data: { label: 'Trajectory Plot', identifier: 'trajectoryPlot', icon: '../../assets/icons/chart-line-24x24.svg' } },
          { data: { label: 'MD => TVD Converter', identifier: 'interpolationTool', icon: '../../assets/icons/trajectories-24x24.svg' } },
        ]
      },
      {
        label: 'UDT', type: 'group', expandedIcon: 'pi pi-folder-open', collapsedIcon: 'pi pi-folder', expanded: true, children: [
          { data: { label: 'UDT Input', identifier: 'undisturbedTemperature', icon: '../../assets/icons/undisturbed-temperature-24x24.svg' } },
          { data: { label: 'UDT Plot', identifier: 'udtPlot', icon: '../../assets/icons/chart-line-24x24.svg' } },
        ]
      },
      {
        label: 'Wellbore Inputs', type: 'group', expandedIcon: 'pi pi-folder-open', collapsedIcon: 'pi pi-folder', expanded: true, children: [
          { data: { label: 'Formation Tops', identifier: 'formationTops', icon: '../../assets/icons/formations-24x24.svg' } },
          { data: { label: 'Fluids', identifier: 'fluids', icon: '../../assets/icons/brines-24x24.svg' } },
          { data: { label: 'Well Configuration', identifier: 'wellConfiguration', icon: '../../assets/icons/well-configuration-24x24.svg' } },
          { data: { label: 'Annulus Contents', identifier: 'annulusContents', icon: '../../assets/icons/load-input-24x24.svg' } },
          { data: { label: 'Well Schematic', identifier: 'wellSchematic', icon: '../../assets/icons/well-schematic-24x24.svg' } },
          { data: { label: 'Cementing & Landing', identifier: 'cementSelector', icon: '../../assets/icons/hard-cement-24x24.svg' } },
          { data: { label: 'Drill String', identifier: 'drillstring', icon: '../../assets/icons/pipes-24x24.svg' } },
          // { data: { label: 'Drill String 2', identifier: 'drillstring2', icon: '../../assets/icons/pipes-24x24.svg' } },
          { data: { label: 'Wellhead', identifier: 'wellhead', icon: '../../assets/icons/pipes-24x24.svg' } },
          { data: { label: 'Packers', identifier: 'packers', icon: '../../assets/icons/packers-24x24.svg' } },
          { data: { label: 'Risers', identifier: 'risers', icon: '../../assets/icons/pipes-24x24.svg' } },
          // { data: { label: 'ESP', identifier: 'esps', icon: '../../assets/icons/pipes-24x24.svg' } },
          // { data: { label: 'Units Convert', identifier: 'unitsConvert', icon: '../../assets/icons/load-input-24x24.svg' } },
        ]
      }
    ];

    this.stressSubMenu = [
      {
        label: 'Inputs', type: 'group', expandedIcon: 'pi pi-folder-open', collapsedIcon: 'pi pi-folder', expanded: true, children: [
          { data: { label: 'Pore Pressure', identifier: 'porePressure', icon: '../../assets/icons/pressures-24x24.svg' } },
          { data: {label: 'Fracture Gradient', identifier: 'fracPressure', icon: '../../assets/icons/pressures-24x24.svg' }},
          { data: {label: 'PPFG Plot', identifier: 'ppfgPlot', icon: '../../assets/icons/chart-line-24x24.svg' }},
          { data: { label: 'Additional Doglegs', identifier: 'additionalDoglegs', icon: '../../assets/icons/trajectories-24x24.svg' } },
          { data: { label: 'PERIVIS Options', identifier: 'stressOptions', icon: '../../assets/icons/settings-24x24.svg' } },
          { data: { label: 'Design Factors', identifier: 'designFactors', icon: '../../assets/icons/settings-24x24.svg' } },
          { data: { label: 'Cementing & Landing', identifier: 'cementSelector', icon: '../../assets/icons/hard-cement-24x24.svg' } },
          { data: { label: 'Packers', identifier: 'packers', icon: '../../assets/icons/packers-24x24.svg' } },
          { data: { label: 'Wellbore Displacement', identifier: 'wellboreDisplacement', icon: '../../assets/icons/load-input-24x24.svg' } },
          { data: { label: 'Pre-Install Loads', identifier: 'preInstallLoads', icon: '../../assets/icons/load-input-24x24.svg' } },
          { data: { label: 'Post-Install Loads', identifier: 'postInstallLoads', icon: '../../assets/icons/load-input-24x24.svg' } },
          { data: { label: 'Load P&T Profiles', identifier: 'pressuresTemperatures', icon: '../../assets/icons/chart-line-24x24.svg' } }
        ]
      },
      {
        label: 'Results', type: 'group', expandedIcon: 'pi pi-folder-open', collapsedIcon: 'pi pi-folder', expanded: true, children: [
          { data: { label: 'Design Limits Plot', identifier: 'designLimitsPlot', icon: '../../assets/icons/Ellipse-24x24.svg' } },
          { data: { label: 'Safety Factors', identifier: 'singleLoadSafetyFactors', icon: '../../assets/icons/chart-line-24x24.svg' } },
          { data: { label: 'Connection SFs', identifier: 'singleConnectionLoadSafetyFactors', icon: '../../assets/icons/chart-line-24x24.svg' } },
          { data: { label: 'Packer Envelope', identifier: 'packerOperatingEnvelope', icon: '../../assets/icons/Ellipse-24x24.svg' } },
          { data: { label: 'Packer Schematic', identifier: 'packerSchematicViewer', icon: '../../assets/icons/packers-24x24.svg' } },
          { data: { label: 'Packer Results Table', identifier: 'packerTableResults', icon: '../../assets/icons/packers-24x24.svg' } },
          { data: { label: 'Press & Temp', identifier: 'pressureResults', icon: '../../assets/icons/pressures-24x24.svg' } },
          { data: { label: 'Barlow Burst', identifier: 'singleLoadBurst', icon: '../../assets/icons/chart-line-24x24.svg' } },
          { data: { label: 'API Collapse', identifier: 'singleLoadCollapse', icon: '../../assets/icons/chart-line-24x24.svg' } },
          { data: { label: 'Axial Forces', identifier: 'singleLoadAxial', icon: '../../assets/icons/chart-line-24x24.svg' } },
          { data: { label: 'Forces/Displacements', identifier: 'displacementResults', icon: '../../assets/icons/chart-line-24x24.svg' } },
          { data: { label: 'Buckling Summary', identifier: 'bucklingSummary', icon: '../../assets/icons/chart-line-24x24.svg' } },
          { data: { label: 'Lift-Off', identifier: 'liftoffResults', icon: '../../assets/icons/chart-line-24x24.svg' } },
          { data: { label: 'Max Allowed Wear', identifier: 'casingWearResults', icon: '../../assets/icons/hard-cement-24x24.svg' } }
        ]
      },
      {
        label: 'Geothermal', type: 'group', expandedIcon: 'pi pi-folder-open', collapsedIcon: 'pi pi-folder', expanded: true, children: [
          { data: { label: 'Post-Yield', identifier: 'postYield', icon: '../../assets/icons/settings-24x24.svg' } },
          { data: { label: 'Holliday Stress Ratio', identifier: 'singleLoadHollidayStressRatio', icon: '../../assets/icons/chart-line-24x24.svg' } },
        ]
      },
    ];

    this.thermalSubMenu = [
      {
        label: 'Inputs', type: 'group', expandedIcon: 'pi pi-folder-open', collapsedIcon: 'pi pi-folder', expanded: true, children: [
          { data: { label: 'Perforations', identifier: 'perforations', icon: '../../assets/icons/perforations-24x24.svg' } },
          { data: { label: 'Gas Lift Valves', identifier: 'gasLiftValves', icon: '../../assets/icons/glv-24x24.svg' } },
          { data: { label: 'Fluids', identifier: 'fluids', icon: '../../assets/icons/brines-24x24.svg' } },
          { data: { label: 'Drill String', identifier: 'drillstring', icon: '../../assets/icons/pipes-24x24.svg' } },
          { data: { label: 'Thermal Operations', identifier: 'thermalOperations', icon: '../../assets/icons/perical-24x24.svg' } },
          { data: { label: 'PERICAL Options', identifier: 'pericalOptions', icon: '../../assets/icons/settings-24x24.svg' } }
        ],
      },
      {
        label: 'Nodal Analysis', type: 'group', expandedIcon: 'pi pi-folder-open', collapsedIcon: 'pi pi-folder', expanded: true, children: [
          { data: { label: 'Chokes Catalog', identifier: 'chokes', icon: '../../assets/icons/choke-full-size.svg' } },
          { data: { label: 'Choke Performance Utility', identifier: 'chokesPerformanceUtility', icon: '../../assets/icons/pressures-24x24.svg' } },
          { data: { label: 'Reservoir Catalog', identifier: 'reservoirCatalog', icon: '../../assets/icons/pressures-24x24.svg' } },
          { data: { label: 'Reservoir Performance Utility', identifier: 'reservoirPerformanceUtility', icon: '../../assets/icons/pressures-24x24.svg' } },
        ]
      },
      {
        label: 'Results', type: 'group', expandedIcon: 'pi pi-folder-open', collapsedIcon: 'pi pi-folder', expanded: true, children: [
          { data: { label: 'Temperatures', identifier: 'thermalTemperatureResults', icon: '../../assets/icons/chart-line-24x24.svg' } },
          { data: { label: 'Pressures', identifier: 'thermalPressureResults', icon: '../../assets/icons/chart-line-24x24.svg' } },
          { data: { label: 'Annular Results', identifier: 'detailedAnnularResults', icon: '../../assets/icons/chart-line-24x24.svg' } },
          { data: { label: 'Operation Envelope', identifier: 'operationEnvelope', icon: '../../assets/icons/chart-line-24x24.svg' } }
        ]
      }
    ];

    this.apbSubMenu = [
      { data: { label: 'Formation Tops', identifier: 'formationTops', icon: '../../assets/icons/formations-24x24.svg' } },
      { data: { label: 'Fracture Gradient', identifier: 'fracPressure', icon: '../../assets/icons/pressures-24x24.svg' } },
      { data: { label: 'APB Inputs', identifier: 'apbInputs', icon: '../../assets/icons/load-input-24x24.svg' } },
      { data: { label: 'APB Results', identifier: 'apbResults', icon: '../../assets/icons/pressures-24x24.svg' } },
      { data: { label: 'APB Results Grid', identifier: 'apbGridResults', icon: '../../assets/icons/chart-line-24x24.svg' } },
    ];

    this.perimotSubMenu = [
      {
        label: 'Inputs', type: 'group', expandedIcon: 'pi pi-folder-open', collapsedIcon: 'pi pi-folder', expanded: true, children: [
          { data: { label: 'Wellhead Movement Inputs', identifier: 'wellheadMovementInputs', icon: '../../assets/icons/load-input-24x24.svg' } }
        ]
      },
      {
        label: 'Results', type: 'group', expandedIcon: 'pi pi-folder-open', collapsedIcon: 'pi pi-folder', expanded: true, children: [
          { data: { label: 'Wellhead Movement Results', identifier: 'wellheadMovementResults', icon: '../../assets/icons/chart-line-24x24.svg' } }
        ]
      }
    ];

    this.isLoading.set(false);
  }

  async ngOnInit(): Promise<void> {
    // Initialize state store
    this.sidebarStore = this._storeService.createStore('sidebar', new SidebarModelUi());
    
    // Initialize signals from persisted store state (synchronous read)
    this.menuSelected.set(this.sidebarStore.state.menuSelected ?? '');
    this.sidebarOpen.set(this.sidebarStore.state.sidebarOpen ?? false);
    
    // Note: We don't need store subscription since we update signals synchronously on user actions.
    // The store is only used for persistence (IndexedDB).
    
    // Notify launcher service of restored sidebar state
    if (this.sidebarStore.state.sidebarOpen) {
      this._launcherSharedService.setData(undefined, undefined, true);
      this.publishGridResizeAfterLayout();
    }

    // If the user lands on the well explorer / designs route, show the well explorer opened.
    await this._route.url.subscribe(routeUrl => {
      if (routeUrl.length == 1 && routeUrl[0].path == "designs") {
        this.setMenuComponent('wellExplorer');
      }
    })

    this.activeItem = this.navbarItems[0];
  }

  public collapseSidenav() {
    this.closeSidenav.emit("close");
  }

  private publishGridResizeAfterLayout(): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this._messenger.publish(new GridItemResizedMessage('none', 0, 0));
      });
    });
  }

  public setMenuComponent(menuComponent: string): void {
    // Get current state to determine new state
    const currentMenuSelected = this.menuSelected();
    const newMenuSelected = currentMenuSelected !== menuComponent ? menuComponent : "";
    const newSidebarOpen = currentMenuSelected !== menuComponent ? true : !this.sidebarOpen();
    
    // Update signals immediately for instant UI response
    this.menuSelected.set(newMenuSelected);
    this.sidebarOpen.set(newSidebarOpen);
    this.scrollToNode.set(!this.scrollToNode());
    
    // Update store (persists to IndexedDB)
    this.sidebarStore.update((state) => ({
      ...state,
      menuSelected: newMenuSelected,
      sidebarOpen: newSidebarOpen
    }));
    
    this._launcherSharedService.setData(undefined, undefined, newSidebarOpen);
    this.publishGridResizeAfterLayout();
  }

  public onDockHover() {
    this.hovered.set(true);
  }

  public onDockLeave() {
    this.hovered.set(false);
  }

  public handleClose() {
    // Update signals immediately for instant UI response
    this.menuSelected.set('');
    this.sidebarOpen.set(false);
    
    // Update store (persists to IndexedDB)
    this.sidebarStore.update((state) => ({
      ...state,
      sidebarOpen: false,
      menuSelected: ''
    }));
    this.publishGridResizeAfterLayout();
  }
}
